import React from 'react';
import { Palette, Monitor, Sun, Moon, Check } from 'lucide-react';
import Breadcrumbs from '../../common/Breadcrumbs';
import { useTheme } from '../../../context/ThemeContext';
import './SettingsDetail.css';

/**
 * Theme & Colors (ST-00127)
 * Route: /settings/organization/branding/theme
 *
 * Three modes — Detect / Light / Dark. Applies instantly on click (no save
 * button): the choice is a per-device display preference held in localStorage,
 * not tenant configuration, so there is nothing to persist server-side.
 */

// The preview swatches are deliberately HARDCODED. The Light card must look
// light while the app is in dark mode and vice versa, so these values cannot
// come from the live token variables. They mirror the real token values.
const PREVIEW = {
  light: {
    shell: '#f9fafb',
    surface: '#ffffff',
    border: '#e5e7eb',
    line: '#d1d5db',
    lineFaint: '#e5e7eb',
    heading: '#9ca3af',
  },
  dark: {
    shell: '#0d1117',
    surface: '#161b22',
    border: '#2b3441',
    line: '#374250',
    lineFaint: '#232c38',
    heading: '#6c7a89',
  },
};

// The sidebar stays dark in both themes — the preview must say so.
const SIDEBAR = '#2a2a2a';
const ACCENT = '#45B7AA';

/** Half of a preview mock: sidebar strip + content surface + text lines. */
const PreviewHalf = ({ palette, clip }) => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      display: 'flex',
      background: palette.shell,
      clipPath: clip,
    }}
  >
    {/* Sidebar strip */}
    <div style={{ width: '22%', background: SIDEBAR, padding: '6px 4px', flexShrink: 0 }}>
      <div style={{ height: 3, borderRadius: 2, background: ACCENT, marginBottom: 6 }} />
      <div
        style={{
          height: 2,
          borderRadius: 2,
          background: 'rgba(255,255,255,0.18)',
          marginBottom: 4,
        }}
      />
      <div
        style={{
          height: 2,
          borderRadius: 2,
          background: 'rgba(255,255,255,0.10)',
          marginBottom: 4,
        }}
      />
      <div style={{ height: 2, borderRadius: 2, background: 'rgba(255,255,255,0.10)' }} />
    </div>

    {/* Content */}
    <div style={{ flex: 1, padding: 7, minWidth: 0 }}>
      <div
        style={{
          background: palette.surface,
          border: `1px solid ${palette.border}`,
          borderRadius: 4,
          padding: 6,
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            height: 3,
            width: '45%',
            borderRadius: 2,
            background: palette.heading,
            marginBottom: 5,
          }}
        />
        <div
          style={{
            height: 2,
            width: '85%',
            borderRadius: 2,
            background: palette.line,
            marginBottom: 3,
          }}
        />
        <div
          style={{
            height: 2,
            width: '70%',
            borderRadius: 2,
            background: palette.lineFaint,
            marginBottom: 3,
          }}
        />
        <div style={{ height: 2, width: '78%', borderRadius: 2, background: palette.lineFaint }} />
      </div>
    </div>
  </div>
);

/** Mini mock of the app shell. `split` renders light and dark side by side. */
const ThemePreview = ({ variant }) => (
  <div
    aria-hidden="true"
    style={{
      position: 'relative',
      width: '100%',
      height: 92,
      borderRadius: 6,
      overflow: 'hidden',
      border: '1px solid var(--border)',
    }}
  >
    {variant === 'split' ? (
      <>
        <PreviewHalf palette={PREVIEW.light} clip="polygon(0 0, 58% 0, 42% 100%, 0 100%)" />
        <PreviewHalf palette={PREVIEW.dark} clip="polygon(58% 0, 100% 0, 100% 100%, 42% 100%)" />
      </>
    ) : (
      <PreviewHalf palette={PREVIEW[variant]} />
    )}
  </div>
);

const OPTIONS = [
  {
    mode: 'detect',
    label: 'Detect',
    hint: 'Follows this device',
    icon: Monitor,
    variant: 'split',
  },
  {
    mode: 'light',
    label: 'Light',
    hint: 'Always light',
    icon: Sun,
    variant: 'light',
  },
  {
    mode: 'dark',
    label: 'Dark',
    hint: 'Always dark',
    icon: Moon,
    variant: 'dark',
  },
];

const ThemeSettings = () => {
  const { mode, resolvedTheme, setMode } = useTheme();

  const breadcrumbItems = [
    { label: 'Organization' },
    { label: 'Branding' },
    { label: 'Theme & Colors' },
  ];

  return (
    <div className="settings-detail">
      <Breadcrumbs items={breadcrumbItems} />

      <div className="settings-detail-container">
        {/* Header */}
        <div className="settings-detail-header">
          <div className="settings-detail-header-icon">
            <Palette size={26} />
          </div>
          <div>
            <h1 className="settings-detail-title">Theme &amp; Colors</h1>
            <p className="settings-detail-description">
              Choose how LexiBridge looks. The change applies immediately.
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="settings-detail-content">
          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <Sun size={20} />
              <h2>Appearance</h2>
            </div>

            <div
              role="radiogroup"
              aria-label="Theme"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                gap: '0.875rem',
              }}
            >
              {OPTIONS.map(({ mode: value, label, hint, icon: Icon, variant }) => {
                const selected = mode === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setMode(value)}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.625rem',
                      padding: '0.75rem',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      borderRadius: '0.625rem',
                      background: selected ? 'var(--accent-soft)' : 'var(--surface)',
                      border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                      boxShadow: selected ? `0 0 0 3px rgb(var(--accent-rgb) / 0.18)` : 'none',
                      transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s',
                    }}
                  >
                    <ThemePreview variant={variant} />

                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Icon
                        size={16}
                        style={{
                          color: selected ? 'var(--accent)' : 'var(--text-muted)',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            color: 'var(--text-strong)',
                          }}
                        >
                          {label}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            fontSize: '0.75rem',
                            color: 'var(--text-muted)',
                          }}
                        >
                          {hint}
                        </span>
                      </span>

                      {selected && (
                        <span
                          aria-hidden="true"
                          style={{
                            marginLeft: 'auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: 'var(--accent)',
                            color: 'var(--text-on-accent)',
                            flexShrink: 0,
                          }}
                        >
                          <Check size={13} strokeWidth={3} />
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="settings-form-hint" style={{ marginTop: '1rem' }}>
              {mode === 'detect'
                ? `Following this device — currently showing the ${resolvedTheme} theme.`
                : `Locked to the ${mode} theme, whatever this device prefers.`}{' '}
              Saved on this device and browser only, so the same account can stay light on a yard
              tablet and dark on an office screen. Everyone else on your organization is unaffected.
            </p>
          </div>

          <div className="settings-card-detail">
            <div className="settings-card-header-detail">
              <Moon size={20} />
              <h2>Good to know</h2>
            </div>
            <ul className="settings-info-list">
              <li>The navigation sidebar stays dark in both themes — it anchors the brand.</li>
              <li>
                Login and splash screens keep their dark treatment regardless of this setting.
              </li>
              <li>
                Generated PDFs and emailed reports are unaffected — they are documents, not screens,
                and always render light.
              </li>
              <li>You can also cycle the theme from the button at the bottom of the sidebar.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeSettings;
